/**
 * Centralized API client with auto-detection and multi-tenant support.
 * All API calls should use this instead of hardcoded URLs.
 *
 * Automatically attaches:
 *  - Authorization: Bearer <token>  (when logged in)
 *  - x-hotel-slug: <slug>           (read from current URL path)
 *  - X-Dining-Session-Code          (for table session guests)
 */

import axios from 'axios';
import { getApiUrl, getApiUrlWithDetection } from './config';

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auto-detect and update base URL on initialization
let isDetecting = false;

async function updateApiUrl() {
  if (isDetecting) return;

  // Only auto-detect if REACT_APP_API_URL is not set
  if (!process.env.REACT_APP_API_URL) {
    isDetecting = true;
    try {
      const detectedUrl = await getApiUrlWithDetection();
      apiClient.defaults.baseURL = detectedUrl;
    } catch (error) {
      console.warn('Auto-detection failed, using default:', error);
    } finally {
      isDetecting = false;
    }
  }
}

// Initialize detection
updateApiUrl();

/**
 * Reads the current hotel slug from the URL path.
 * Works without React context so it can be used in an interceptor.
 * e.g. /hotel/shreyas/admin → "shreyas"
 */
function getHotelSlugFromUrl() {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/hotel\/([^/]+)/);
  return match ? match[1] : null;
}

// Apply request interceptor to default axios instance too
axios.interceptors.request.use(
  (config) => {
    // Auth token
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Multi-tenant hotel slug header
    // Skip for super admin paths — they have no hotel context
    const isSuperAdminPath = window.location.pathname.startsWith('/sys-panel-7x9k');
    if (!isSuperAdminPath && !config.headers['x-hotel-slug']) {
      const slug = getHotelSlugFromUrl();
      if (slug) {
        config.headers['x-hotel-slug'] = slug;
      }
    }

    // Dining session (for table guests who aren't logged in)
    const diningSession = JSON.parse(localStorage.getItem('diningSession') || 'null');
    const diningMember = JSON.parse(localStorage.getItem('diningMember') || 'null');
    if (!token && diningSession?.sessionCode) {
      if (!config.headers['X-Dining-Session-Code']) {
        config.headers['X-Dining-Session-Code'] = diningSession.sessionCode;
      }
      if (diningMember?.id && !config.headers['X-Dining-Member-Id']) {
        config.headers['X-Dining-Member-Id'] = diningMember.id;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Apply response interceptor to default axios instance too
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 402 = Subscription expired or inactive
    if (status === 402) {
      const slug = getHotelSlugFromUrl();
      const code = error.response?.data?.code || 'SUBSCRIPTION_EXPIRED';
      window.location.href = slug
        ? `/hotel/${slug}/subscription-expired?reason=${code}`
        : '/subscription-expired';
      return Promise.reject(error);
    }

    // 401 = Not authenticated
    if (status === 401 && !localStorage.getItem('diningSession')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const slug = getHotelSlugFromUrl();
      window.location.href = slug ? `/hotel/${slug}/login` : '/login';
    }

    return Promise.reject(error);
  }
);

// ── Request interceptor (apiClient) ────────────────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    // Auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Multi-tenant hotel slug header
    // Skip for super admin paths — they have no hotel context
    const isSuperAdminPath = window.location.pathname.startsWith('/sys-panel-7x9k');
    if (!isSuperAdminPath) {
      const slug = getHotelSlugFromUrl();
      if (slug) {
        config.headers['x-hotel-slug'] = slug;
      }
    }

    // Dining session (for table guests who aren't logged in)
    const diningSession = JSON.parse(localStorage.getItem('diningSession') || 'null');
    const diningMember = JSON.parse(localStorage.getItem('diningMember') || 'null');
    if (!token && diningSession?.sessionCode) {
      config.headers['X-Dining-Session-Code'] = diningSession.sessionCode;
      if (diningMember?.id) {
        config.headers['X-Dining-Member-Id'] = diningMember.id;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ── Response interceptor (apiClient) ───────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 402 = Subscription expired or inactive
    if (status === 402) {
      const slug = getHotelSlugFromUrl();
      const code = error.response?.data?.code || 'SUBSCRIPTION_EXPIRED';
      window.location.href = slug
        ? `/hotel/${slug}/subscription-expired?reason=${code}`
        : '/subscription-expired';
      return Promise.reject(error);
    }

    // 401 = Not authenticated
    if (status === 401 && !localStorage.getItem('diningSession')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const slug = getHotelSlugFromUrl();
      window.location.href = slug ? `/hotel/${slug}/login` : '/login';
    }

    return Promise.reject(error);
  }
);

export default apiClient;
